/**
 * Canonical product registry for the corporate site.
 *
 * The CMS is the source of truth: `services/api.ts` fetches /platform-products
 * and maps the response onto these types. This file is the
 * fallback that renders when the API is unreachable OR returns a record missing
 * the fields a page needs — a half-populated product is worse than a static one,
 * because it ships a page with empty sections to a buyer.
 *
 * Keep the shape here and the CMS schema in step. When a field is added to
 * ProductItem it also needs adding to the admin CMS model and to its field
 * whitelist, or the CMS will silently drop it on save.
 */

export type ProductCategory =
    | "AI Agents"
    | "Computer Vision"
    | "DevOps & Cloud"
    | "Messaging & Comms"
    | "Hospitality & CRM";

/** A capability worth a line on the detail page. */
export interface ProductFeature {
    title: string;
    description: string;
}

/**
 * A single operational number. `value` is deliberately a string: these are
 * display figures ("<300ms", "99.8%", "10x"), not quantities to compute with.
 */
export interface ProductMetric {
    value: string;
    label: string;
}

export interface ProductItem {
    /** URL segment. Stable — it is the shareable link and the sitemap entry. */
    slug: string;
    name: string;
    category: ProductCategory;
    /** One line, used on cards and in the mega-menu. */
    tagline: string;
    /** Two or three sentences, used on the detail page and the homepage tabs. */
    summary: string;
    /** What is broken before this product exists. */
    problem: string;
    /** What changes once it does. */
    solution: string;
    features: ProductFeature[];
    metrics: ProductMetric[];
    /** Named technologies, rendered as badges. */
    architecture: string[];
    /** Live app. Absent when a product has no public entry point. */
    subdomain?: string;
    /** Shown on the Sign In menu only when the product has a tenant-facing login. */
    hasSignIn: boolean;
    accent: string;
}

export interface SolutionItem {
    slug: string;
    title: string;
    description: string;
    /** Products that serve this solution, by slug. */
    productSlugs: string[];
}

/**
 * Accents are assigned per product line so a card, its tab, and its detail page
 * agree without a lookup table elsewhere. They are signal, not decoration: the
 * category owns the hue, the product owns the step.
 */
export const PRODUCTS: ProductItem[] = [
    {
        slug: "social-ai",
        name: "MindSyncX Social AI",
        category: "AI Agents",
        tagline: "Social media operations that run themselves, with a human on the gate.",
        summary:
            "Multi-tenant B2B social automation built LinkedIn-first. It discovers what your market is talking about, drafts posts in your voice, routes them through an approval gate, and distributes the approved ones through your employees' own networks.",
        problem:
            "Social output stalls between the person with the idea and the person allowed to publish. Approval lives in chat threads, employee advocacy is a spreadsheet nobody updates, and by the time a post ships the trend has moved.",
        solution:
            "Trend discovery, drafting, approval and advocacy in one pipeline. Every post carries its approval trail, so publishing fast and publishing carefully stop being opposites.",
        features: [
            { title: "Trend discovery", description: "Surfaces what your market is posting about now, ranked by movement rather than volume." },
            { title: "AI drafting", description: "Generates posts against your brand voice and prior performance, not a generic template." },
            { title: "Approval gates", description: "Nothing publishes without the sign-off the account requires. The trail is the audit record." },
            { title: "Employee advocacy", description: "Distributes approved posts through employees' own networks, with per-person opt-in." },
        ],
        metrics: [
            { value: "10x", label: "Publishing throughput" },
            { value: "Multi-tenant", label: "Isolated per client" },
            { value: "LinkedIn-first", label: "Primary channel" },
        ],
        architecture: ["React", "Node.js", "MongoDB", "LinkedIn API", "LLM orchestration"],
        subdomain: "https://social.mindsyncx.ai",
        hasSignIn: true,
        accent: "#BED743",
    },
    {
        slug: "syncx-ai",
        name: "SyncX AI & Orchestrator",
        category: "AI Agents",
        tagline: "A support chatbot, a RAG knowledge base, and the graph that wires them together.",
        summary:
            "Two halves of the same system: a customer support widget grounded in your own documentation, and a visual node-based builder for the multi-agent workflows behind it. Build the flow, watch it run, change it without a deploy.",
        problem:
            "Chatbots either answer from a script and frustrate people, or answer from a model and invent things. Changing what one does means a developer, a ticket, and a release.",
        solution:
            "Answers are grounded in your knowledge base and cited. The workflow behind them is a graph you edit directly, so the people who own the process own the logic.",
        features: [
            { title: "Support widget", description: "Drops into any site and answers from your documentation, with citations." },
            { title: "Enterprise RAG", description: "Retrieval grounded in your corpus, so answers can be traced to a source." },
            { title: "Visual workflow builder", description: "Node-based multi-agent flows you edit and reason about as a graph." },
            { title: "Live iteration", description: "Change a flow and see the effect without redeploying the application." },
        ],
        metrics: [
            { value: "<300ms", label: "Median response" },
            { value: "Cited", label: "Every answer" },
            { value: "No-deploy", label: "Flow changes" },
        ],
        architecture: ["React", "@xyflow/react", "Vector search", "Python", "FastAPI"],
        subdomain: "https://syncx.mindsyncx.ai",
        hasSignIn: true,
        accent: "#CBE05F",
    },
    {
        slug: "workmate-ai",
        name: "WorkMate AI",
        category: "AI Agents",
        tagline: "Ask your documents a question and get an answer you can check.",
        summary:
            "Enterprise document intelligence across ten-plus LLM providers. It parses PDF, DOCX and scanned images through OCR, retrieves with a hybrid of vector and BM25 search, and cites the passage every claim came from.",
        problem:
            "The answer is in a 300-page PDF nobody has time to read, and a model that summarises it without citations cannot be trusted with anything that matters.",
        solution:
            "Hybrid retrieval finds the passage, the model answers from it, and the citation points back so a reader can verify in one click. Provider choice stays yours.",
        features: [
            { title: "10+ LLM providers", description: "Switch provider per workspace without reindexing the corpus." },
            { title: "Multi-format parsing", description: "PDF, DOCX and scanned documents through OCR, in one pipeline." },
            { title: "Hybrid retrieval", description: "Vector similarity and BM25 together, because neither alone finds everything." },
            { title: "Citations", description: "Every claim links to the passage it came from." },
            { title: "Adaptive quizzes", description: "Generates comprehension checks from the source material for training use." },
        ],
        metrics: [
            { value: "10+", label: "LLM providers" },
            { value: "Hybrid", label: "Vector + BM25" },
            { value: "Cited", label: "Every claim" },
        ],
        architecture: ["Python", "FastAPI", "Vector DB", "BM25", "OCR", "React"],
        subdomain: "https://workmate.mindsyncx.ai",
        hasSignIn: true,
        accent: "#A3BC2E",
    },
    {
        slug: "vass-vision",
        name: "VASS Vision AI",
        category: "Computer Vision",
        tagline: "Attendance and surveillance from the cameras already on the wall.",
        summary:
            "Face recognition attendance and video assistance over live RTSP streams, using SCRFD detection and ArcFace embedding with liveness checks that reject a photograph held up to the lens.",
        problem:
            "Badge systems record that a card was present, not a person. Existing camera estate goes unused because the software to make it useful is a separate procurement.",
        solution:
            "Recognition runs against the RTSP streams you already have. Liveness anti-spoofing separates a face from a picture of one, so attendance means attendance.",
        features: [
            { title: "SCRFD detection", description: "Locates faces in frame under real lighting, not benchmark conditions." },
            { title: "ArcFace recognition", description: "Identity from embedding distance, tuned for enrollment sets that grow." },
            { title: "Liveness anti-spoofing", description: "Rejects a printed photo or a phone screen presented to the camera." },
            { title: "RTSP processing", description: "Works with the existing camera estate rather than replacing it." },
        ],
        metrics: [
            { value: "99.8%", label: "Recognition accuracy" },
            { value: "Live RTSP", label: "Stream processing" },
            { value: "Anti-spoof", label: "Liveness checked" },
        ],
        architecture: ["Python", "SCRFD", "ArcFace", "OpenCV", "RTSP", "PyTorch"],
        subdomain: "https://vass.mindsyncx.ai",
        hasSignIn: false,
        accent: "#7DD3C0",
    },
    {
        slug: "infra-monitor",
        name: "InfraMonitor",
        category: "DevOps & Cloud",
        tagline: "Fleet telemetry and a terminal, without installing an agent.",
        summary:
            "Multi-tenant infrastructure monitoring with native Prometheus charts, per-container Docker metrics, automated alerting, and brokered SSH in the browser through Apache Guacamole.",
        problem:
            "Seeing a problem and fixing it happen in different tools. The dashboard says a container is unhealthy; fixing it means finding the right jump host and the right key.",
        solution:
            "The chart and the shell are the same screen. Metrics come from Prometheus directly, and SSH is brokered so access is granted and revoked centrally.",
        features: [
            { title: "Native Prometheus", description: "Charts query Prometheus directly rather than a copy of it." },
            { title: "Container metrics", description: "Per-container CPU, memory and I/O across the fleet." },
            { title: "Automated alerts", description: "Thresholds that route to the people on call for that tenant." },
            { title: "Brokered SSH", description: "In-browser shell via Apache Guacamole, with access granted centrally." },
        ],
        metrics: [
            { value: "Zero-agent", label: "No install per host" },
            { value: "Multi-tenant", label: "Isolated per client" },
            { value: "In-browser", label: "Brokered SSH" },
        ],
        architecture: ["Prometheus", "Docker", "Apache Guacamole", "Node.js", "React"],
        subdomain: "https://infra.mindsyncx.com",
        hasSignIn: true,
        accent: "#6BA8E5",
    },
    {
        slug: "atithione-crm",
        name: "AtithiOne Hospitality CRM",
        category: "Hospitality & CRM",
        tagline: "The hospitality sales pipeline, including the part that happens in a car park.",
        summary:
            "End-to-end hospitality sales CRM with a corporate booking pipeline, plus a field mobile app with GPS check-in and business-card OCR for reps working outside the office.",
        problem:
            "Hospitality sales happens on the road. By the time a rep is back at a desk to log the visit, the detail is gone and the card is in a jacket pocket.",
        solution:
            "The visit is logged where it happens. GPS check-in records the call, OCR turns the card into a contact, and the corporate pipeline stays current without an evening of data entry.",
        features: [
            { title: "Corporate booking pipeline", description: "Tracks a corporate account from enquiry through contracted rate." },
            { title: "GPS field check-in", description: "Records the visit at the property, at the time it happened." },
            { title: "Business card OCR", description: "Card to contact record without retyping." },
            { title: "Field mobile app", description: "Built for reps who are rarely at a desk." },
        ],
        metrics: [
            { value: "Field-first", label: "Mobile + web" },
            { value: "GPS", label: "Verified check-in" },
            { value: "OCR", label: "Card capture" },
        ],
        architecture: ["React", "React Native", "Node.js", "MySQL", "OCR"],
        subdomain: "https://atithione.com",
        hasSignIn: true,
        accent: "#E58B6B",
    },
    {
        slug: "voice-agent",
        name: "MindSyncX Voice Agent",
        category: "AI Agents",
        tagline: "A voice agent that holds a real conversation, on the phone or in a browser.",
        summary:
            "Real-time spoken conversation over a phone line or in the browser, on a cascade pipeline of Deepgram Nova-3 for speech, xAI Grok for reasoning and Inworld for voice. It can be interrupted mid-sentence, measures its own latency turn by turn, and writes a full transcript of every call.",
        problem:
            "Phone bots talk over you, cannot be interrupted, and leave nothing behind but a call duration. Nobody can say what a conversation cost or why a particular turn took four seconds.",
        solution:
            "Barge-in means a caller can cut in the way they would with a person. Every turn is timed and every call is transcribed, so a slow response is something you can look at rather than something you suspect.",
        features: [
            { title: "Barge-in", description: "A caller can interrupt mid-sentence and the agent stops and listens." },
            { title: "Per-turn latency", description: "Each turn is measured, so a slow exchange can be traced to the stage that caused it." },
            { title: "Full transcript", description: "Every call is written down, not summarised." },
            { title: "Phone or browser", description: "The same agent answers a phone line or a web console." },
        ],
        metrics: [
            { value: "$0.020/min", label: "Conversation cost" },
            { value: "Barge-in", label: "Interruptible" },
            { value: "Per-turn", label: "Latency measured" },
        ],
        architecture: ["Deepgram Nova-3", "xAI Grok", "Inworld TTS", "Python", "Docker"],
        hasSignIn: false,
        accent: "#7A8C1B",
    },
    {
        slug: "business-messaging",
        name: "MindSyncX Business Messaging",
        category: "Messaging & Comms",
        tagline: "Self-hosted WhatsApp for businesses that need to own the conversation.",
        summary:
            "A multi-organisation WhatsApp platform running on your own infrastructure. Two channels — QR-paired sessions and the WhatsApp Business Cloud API — behind one org-scoped API, with per-organisation keys and a live two-way inbox.",
        problem:
            "Hosted messaging vendors hold your conversation history, price per seat, and decide what your integration is allowed to do. For a business whose customer relationship runs through WhatsApp, that is the relationship sitting on someone else's server.",
        solution:
            "The platform runs on your infrastructure with your database. Sessions are org-scoped, each organisation gets its own API keys, and the inbox is two-way — so support answers in the same thread the customer started.",
        features: [
            { title: "Multi-organisation", description: "One deployment serves many organisations, scoped and isolated." },
            { title: "Two channels", description: "QR-paired sessions and the WhatsApp Business Cloud API behind one interface." },
            { title: "Live inbox", description: "Two-way chat, so a reply lands in the conversation the customer opened." },
            { title: "Per-org API keys", description: "Each organisation integrates on its own credentials." },
        ],
        metrics: [
            { value: "Self-hosted", label: "Your infrastructure" },
            { value: "Multi-org", label: "Scoped per tenant" },
            { value: "Two-way", label: "Live inbox" },
        ],
        architecture: ["NestJS", "React", "Supabase Postgres", "Redis", "Baileys"],
        subdomain: "https://messaging.mindsyncx.ai",
        hasSignIn: true,
        accent: "#7FB069",
    },
];

export const SOLUTIONS_BY_INDUSTRY: SolutionItem[] = [
    {
        slug: "hospitality-travel",
        title: "Hospitality & Travel",
        description:
            "Corporate booking pipelines that stay current from the field, and a concierge agent that answers guests before the front desk has to.",
        productSlugs: ["atithione-crm", "syncx-ai"],
    },
    {
        slug: "b2b-saas-tech",
        title: "B2B SaaS & Technology",
        description:
            "Social output at the pace of the market, support that answers from your own docs, and fleet telemetry for the infrastructure underneath.",
        productSlugs: ["social-ai", "syncx-ai", "infra-monitor"],
    },
    {
        slug: "healthcare-life-sciences",
        title: "Healthcare & Life Sciences",
        description:
            "Document intelligence over clinical and regulatory material, with every answer traceable to the passage it came from.",
        productSlugs: ["workmate-ai"],
    },
    {
        slug: "corporate-campuses",
        title: "Corporate Campuses & Facilities",
        description:
            "Attendance and surveillance from the existing camera estate, with liveness checks that make the record mean something.",
        productSlugs: ["vass-vision", "infra-monitor"],
    },
];

export const SOLUTIONS_BY_CAPABILITY: SolutionItem[] = [
    {
        slug: "autonomous-social-growth",
        title: "Autonomous social growth",
        description: "Discovery, drafting, approval and advocacy as one pipeline instead of four tools.",
        productSlugs: ["social-ai"],
    },
    {
        slug: "enterprise-document-rag",
        title: "Enterprise document RAG",
        description: "Hybrid retrieval over your own corpus, answering with citations a reader can check.",
        productSlugs: ["workmate-ai", "syncx-ai"],
    },
    {
        slug: "intelligent-video-surveillance",
        title: "Intelligent video surveillance",
        description: "Recognition and liveness over live RTSP, on the cameras already installed.",
        productSlugs: ["vass-vision"],
    },
    {
        slug: "devops-fleet-observability",
        title: "DevOps fleet observability",
        description: "Prometheus-native charts and brokered SSH on one screen, with no agent to install.",
        productSlugs: ["infra-monitor"],
    },
];

export const PRODUCT_CATEGORIES: ProductCategory[] = [
    "AI Agents",
    "Computer Vision",
    "DevOps & Cloud",
    "Messaging & Comms",
    "Hospitality & CRM",
];

export const getProductBySlug = (slug: string): ProductItem | undefined =>
    PRODUCTS.find((p) => p.slug === slug);

export const getProductsByCategory = (category: ProductCategory): ProductItem[] =>
    PRODUCTS.filter((p) => p.category === category);

/**
 * The product's live URL, or undefined when one is not configured.
 *
 * Every "Launch app" control is gated on this rather than on the raw field
 * being truthy: an empty string is already handled by truthiness, but a value
 * of " " or "tbd" is not, and either would render a link that goes nowhere. A
 * launch button is a promise that something is there to open, so it appears
 * only when the backend holds a real http(s) URL.
 */
export const launchUrl = (product: ProductItem): string | undefined => {
    const url = product.subdomain?.trim();
    return url && /^https?:\/\//i.test(url) ? url : undefined;
};

/** Products with a tenant-facing login, for the Sign In menu. */
export const getSignInProducts = (source: ProductItem[] = PRODUCTS): ProductItem[] =>
    source.filter((p) => p.hasSignIn && launchUrl(p));
